import React from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Label,
  Customized
} from 'recharts';
import { formatPlaceName } from '@/lib/utils';

interface ChartDataItem {
  name: string;
  year?: number;
  popGrowth: number;
  housingGrowth: number;
  prevYear?: number;
  prevPopGrowth?: number;
  prevHousingGrowth?: number;
  isSelected: boolean;
}

interface PopulationVsHousingGrowthChartProps {
  data: ChartDataItem[];
  selectedColor?: string;
  peerColor?: string;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const status = data.housingGrowth > data.popGrowth ? 'Covered' : 'Under Stress';
    return (
      <div className="bg-white p-2 border border-gray-300 rounded shadow-lg">
        <p className="font-semibold">
          {formatPlaceName(data.name)} ({data.year})
        </p>
        <p>Population Growth: {data.popGrowth.toFixed(2)}%</p>
        <p>Housing Growth: {data.housingGrowth.toFixed(2)}%</p>
        <p className={`font-semibold ${status === 'Covered' ? 'text-green-600' : 'text-red-600'}`}>
          Status: {status}
        </p>
      </div>
    );
  }
  return null;
};

const EquilibriumLine = ({ xAxisMap, yAxisMap }: any) => {
  if (!xAxisMap || !yAxisMap) return null;

  const xAxis = xAxisMap[0];
  const yAxis = yAxisMap[0];
  
  if (!xAxis || !yAxis || typeof xAxis.scale !== 'function' || typeof yAxis.scale !== 'function' || !xAxis.range || !yAxis.range) {
    return null;
  }

  const xDomain = xAxis.scale.domain();
  const yDomain = yAxis.scale.domain();

  if (!xDomain || xDomain.length < 2 || !yDomain || yDomain.length < 2) {
    return null;
  }
  
  const min = Math.min(xDomain[0], yDomain[0]);
  const max = Math.max(xDomain[1], yDomain[1]);
  
  const p1 = { x: xAxis.scale(min), y: yAxis.scale(min) };
  const p2 = { x: xAxis.scale(max), y: yAxis.scale(max) };
  
  if (isNaN(p1.x) || isNaN(p1.y) || isNaN(p2.x) || isNaN(p2.y)) {
    return null;
  }

  const chartTopY = yAxis.range[1];
  const chartBottomY = yAxis.range[0];
  const chartLeftX = xAxis.range[0];
  const chartRightX = xAxis.range[1];

  return (
    <g>
      <line
        x1={p1.x}
        y1={p1.y}
        x2={p2.x}
        y2={p2.y}
        stroke="#a0aec0"
        strokeWidth={1}
        strokeDasharray="5 5"
      />
      <text x={chartLeftX + 10} y={chartTopY + 20} fill="#a0aec0" fontSize="12" textAnchor="start">
        Covered
      </text>
      <text x={chartRightX - 10} y={chartBottomY - 10} fill="#a0aec0" fontSize="12" textAnchor="end">
        Unleveraged
      </text>
    </g>
  );
};

const TrendArrows = ({ data, xAxisMap, yAxisMap, coveredColor, unleveragedColor }: any) => {
  if (!xAxisMap || !yAxisMap) return null;

  const xAxis = xAxisMap[0];
  const yAxis = yAxisMap[0];

  return (
    <g>
      {data.map((d: ChartDataItem, index: number) => {
        if (d.prevPopGrowth === undefined || d.prevHousingGrowth === undefined) {
          return null;
        }

        const x1 = xAxis.scale(d.prevPopGrowth);
        const y1 = yAxis.scale(d.prevHousingGrowth);
        let x2 = xAxis.scale(d.popGrowth);
        let y2 = yAxis.scale(d.housingGrowth);

        if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) {
          return null;
        }

        const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
        const shorteningFactor = 5; // pixels to shorten the line by
        
        if (length > shorteningFactor) {
          const ratio = (length - shorteningFactor) / length;
          x2 = x1 + (x2 - x1) * ratio;
          y2 = y1 + (y2 - y1) * ratio;
        }

        const startStatus = d.prevHousingGrowth > d.prevPopGrowth ? 'Covered' : 'Unleveraged';
        const endStatus = d.housingGrowth > d.popGrowth ? 'Covered' : 'Unleveraged';
        
        const startColor = startStatus === 'Covered' ? coveredColor : unleveragedColor;
        const endColor = endStatus === 'Covered' ? coveredColor : unleveragedColor;
        const gradientId = `arrow-gradient-${index}`;

        return (
          <React.Fragment key={`arrow-fragment-${index}`}>
            <defs>
              <linearGradient id={gradientId} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={startColor} />
                <stop offset="100%" stopColor={endColor} />
              </linearGradient>
            </defs>
            <line
              key={`arrow-line-${index}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={`url(#${gradientId})`}
              strokeWidth={4}
              markerEnd={`url(#arrow-marker-${index})`}
            />
            <marker
              id={`arrow-marker-${index}`}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={endColor} />
            </marker>
          </React.Fragment>
        );
      })}
    </g>
  );
};

export const PopulationVsHousingGrowthChart: React.FC<PopulationVsHousingGrowthChartProps> = ({
  data,
}) => {
  if (!data || data.length === 0) {
    return <div className="flex items-center justify-center h-full text-gray-500">No data available for chart.</div>;
  }

  const coveredColor = '#22c55e'; // green-500
  const unleveragedColor = '#ef4444'; // red-500

  // Add a 'size' property for ZAxis, e.g., based on population or fixed
  const processedData = data.map(d => ({
    ...d,
    size: d.isSelected ? 200 : 60,
    status: d.housingGrowth > d.popGrowth ? 'Covered' : 'Unleveraged',
  }));
  const prevYearData = data
    .filter(d => d.prevPopGrowth !== undefined && d.prevHousingGrowth !== undefined)
    .map(d => ({
      ...d,
      year: d.prevYear,
      popGrowth: d.prevPopGrowth,
      housingGrowth: d.prevHousingGrowth,
      size: d.isSelected ? 200 : 60,
      status: d.prevHousingGrowth! > d.prevPopGrowth! ? 'Covered' : 'Unleveraged',
    }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ScatterChart
        margin={{
          top: 20,
          right: 20,
          bottom: 40,
          left: 60,
        }}
      >
        <defs>
          <marker
            id="arrow-covered"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={coveredColor} />
          </marker>
          <marker
            id="arrow-unleveraged"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={unleveragedColor} />
          </marker>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          type="number" 
          dataKey="popGrowth" 
          name="Population Growth" 
          unit="%"
        >
            <Label value="Population Growth (%)" offset={-25} position="insideBottom" />
        </XAxis>
        <YAxis 
          type="number" 
          dataKey="housingGrowth" 
          name="Housing Stock Growth" 
          unit="%"
        >
            <Label value="Housing Stock Growth (%)" offset={-10} angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
        </YAxis>
        <ZAxis type="number" dataKey="size" range={[20, 80]} />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
        <Customized component={<EquilibriumLine />} />
        <Customized component={<TrendArrows data={data} coveredColor={coveredColor} unleveragedColor={unleveragedColor} />} />
        <Scatter name="Previous Year" data={prevYearData} fillOpacity={0.6}>
          {prevYearData.map((entry, index) => (
            <Cell key={`cell-prev-${index}`} fill={entry.status === 'Covered' ? coveredColor : unleveragedColor} />
          ))}
        </Scatter>
        <Scatter name="Cities" data={processedData}>
          {processedData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.status === 'Covered' ? coveredColor : unleveragedColor}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}; 